import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TrendingUp, Coins, Gem, SlidersHorizontal, Settings, LogOut, ArrowLeftRight, BarChart3, Layers, Sparkles, Link2, ShieldCheck, Sun, Moon, KeyRound } from "lucide-react";
import { API } from "../utils/helpers";
import { styles } from "./admin/adminStyles";
import "../AdminTheme.css";
import MarjTab from "./admin/MarjTab";
import TickerTab from "./admin/TickerTab";
import ArayuzTab from "./admin/ArayuzTab";
import AlarmTab from "./admin/AlarmTab";
import EpostaTab from "./admin/EpostaTab";
import KullaniciTab from "./admin/KullaniciTab";
import AltinkaynakCarpanTab from "./admin/AltinkaynakCarpanTab";
import FiyatKaynagiTab from "./admin/FiyatKaynagiTab";
import FiyatGuvenligiTab from "./admin/FiyatGuvenligiTab";
import ErisimKodlariTab from "./admin/ErisimKodlariTab";

const NAV_ITEMS = [
  { id: 'altinkaynak_carpan', label: 'Altinkaynak HAS', icon: Sparkles },
  { id: 'fiyat_kaynagi', label: 'Fiyat Kaynagi', icon: Link2 },
];

const ESKI_MODLAR_ITEMS = [];

const DIGER_ITEMS = [
  { id: 'ticker', label: 'Kayar Band', icon: BarChart3 },
  { id: 'arayuz', label: 'Arayuz', icon: Layers },
  { id: 'fiyat_guvenligi', label: 'Fiyat Guvenligi', icon: ShieldCheck },
  { id: 'alarm', label: 'Alarm', icon: Settings },
  { id: 'eposta', label: 'E-posta', icon: Settings },
];

const SIDEBAR_W = 220;
const BP = 1024;

const AdminPage = () => {
  // Auth state
  const [loggedIn, setLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginState, setLoginState] = useState({ locked: false, lockSeconds: 0, requiresVerification: false, remaining: null });
  const [verificationCode, setVerificationCode] = useState("");

  // Access state
  const [accessType, setAccessType] = useState(null); // "branch" | "ana" | "master"
  const [profile, setProfile] = useState(null);
  const [availableProfiles, setAvailableProfiles] = useState([]);

  // Admin state - varsayilan tab: Altinkaynak HAS
  const [activeNav, setActiveNav] = useState("altinkaynak_carpan");
  const [eskiModlarOpen, setEskiModlarOpen] = useState(false);  // Eski modlar accordion
  const [users, setUsers] = useState([]);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= BP : true);
  const [activePricingMode, setActivePricingMode] = useState(null);
  const [priceSource, setPriceSource] = useState({ source_profile: null, apply_marj: true });
  const [adminTheme, setAdminTheme] = useState(() => {
    try { return localStorage.getItem('qen_admin_theme') === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
  });
  const toggleAdminTheme = () => {
    setAdminTheme(t => {
      const n = t === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('qen_admin_theme', n); } catch {}
      return n;
    });
  };
  const rootClass = `admin-root admin-${adminTheme}`;
  const ThemeToggleBtn = ({ style }) => (
    <button type="button" onClick={toggleAdminTheme} data-testid="admin-theme-toggle" title={adminTheme === 'dark' ? 'Açık tema' : 'Koyu tema'}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, border: '1px solid var(--adm-border, #2c3a33)', background: adminTheme === 'dark' ? '#16201b' : '#e4e4e4', color: '#DA9F54', cursor: 'pointer', ...style }}>
      {adminTheme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );

  const loadPriceSource = useCallback(async () => {
    if (!profile) return;
    try {
      const res = await axios.get(`${API}/admin/price-source?profile=${profile}`);
      setPriceSource({
        source_profile: res.data.source_profile,
        apply_marj: res.data.apply_marj !== false,
      });
    } catch (err) { console.error("loadPriceSource error:", err); }
  }, [profile]);

  useEffect(() => { loadPriceSource(); }, [loadPriceSource, activeNav]);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users || []);
    } catch (err) { console.error("loadUsers error:", err); }
  };

  useEffect(() => { loadUsers(); }, []);

  // Auto logout after 3 hours
  useEffect(() => {
    if (!loggedIn) return;
    const timer = setTimeout(() => { setLoggedIn(false); setProfile(null); setAccessType(null); setPassword(""); }, 3 * 60 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [loggedIn]);

  // Lock countdown
  useEffect(() => {
    if (!loginState.locked || loginState.lockSeconds <= 0) return;
    const timer = setInterval(() => {
      setLoginState(prev => {
        const newSecs = prev.lockSeconds - 1;
        if (newSecs <= 0) return { ...prev, locked: false, lockSeconds: 0 };
        return { ...prev, lockSeconds: newSecs };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [loginState.locked, loginState.lockSeconds]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loginLoading) return;
    setLoginLoading(true);
    setError("");
    try {
      const body = { password };
      if (loginState.requiresVerification && verificationCode) {
        body.verification_code = verificationCode;
      }
      const res = await axios.post(`${API}/auth/admin-login`, body);
      if (res.data.success) {
        const type = res.data.type; // "branch", "ana", "master"
        setAccessType(type);
        setLoginState({ locked: false, lockSeconds: 0, requiresVerification: false, remaining: null });
        setVerificationCode("");

        if (type === "branch") {
          // Aktif pricing mode'u al, ondan sonra login bayragini ac
          let activePm = "altinkaynak_carpan";
          try {
            const pmRes = await axios.get(`${API}/admin/pricing-mode?profile=${res.data.profile}`);
            activePm = pmRes.data.active_mode || "altinkaynak_carpan";
          } catch (err) { console.error("loadPricingMode (branch) error:", err); }
          setActivePricingMode(activePm);
          // Varsayilan tab: her zaman Altinkaynak HAS (kullanici istegi)
          setActiveNav("altinkaynak_carpan");
          if (['ozan_marj', 'tl_iscilik', 'has', 'ozel'].includes(activePm)) {
            setEskiModlarOpen(true);  // Eski moddaysa accordion'u acik tut
          }
          setProfile(res.data.profile);
          setLoggedIn(true);
        } else {
          // "ana" or "master" - show branch selector
          setAvailableProfiles(res.data.profiles || []);
        }
      } else if (res.data.locked) {
        setLoginState(prev => ({ ...prev, locked: true, lockSeconds: res.data.lock_seconds || 300 }));
        setError(res.data.message);
      } else if (res.data.requires_verification) {
        setLoginState(prev => ({ ...prev, requiresVerification: true }));
        setError(res.data.message);
      } else {
        setError(res.data.message || "Sifre yanlis!");
        if (res.data.remaining !== undefined) setLoginState(prev => ({ ...prev, remaining: res.data.remaining }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || "Giris hatasi!");
    } finally {
      setLoginLoading(false);
    }
  };

  const selectProfile = async (p) => {
    // Aktif pricing modunu ONCE al, sonra login
    let activePm = "altinkaynak_carpan";
    try {
      const res = await axios.get(`${API}/admin/pricing-mode?profile=${p}`);
      activePm = res.data.active_mode || "altinkaynak_carpan";
    } catch (err) { console.error("loadPricingMode (selectProfile) error:", err); }
    setActivePricingMode(activePm);
    // Varsayilan tab: her zaman Altinkaynak HAS
    setActiveNav("altinkaynak_carpan");
    if (['ozan_marj', 'tl_iscilik', 'has', 'ozel'].includes(activePm)) {
      setEskiModlarOpen(true);
    }
    setProfile(p);
    setLoggedIn(true);
  };

  const handleLogout = () => {
    setLoggedIn(false);
    setProfile(null);
    setAccessType(null);
    setPassword("");
    setAvailableProfiles([]);
    setActiveNav("altinkaynak_carpan");
  };

  const handleBackToProfiles = () => {
    setProfile(null);
    setLoggedIn(false);
    setActiveNav("altinkaynak_carpan");
  };

  // Step 1: Password entry
  if (!accessType) {
    return (
      <div className={rootClass} style={styles.container}>
        <div style={styles.loginBox}>
          <ThemeToggleBtn style={{ position: 'absolute', top: 16, right: 16, width: 34, height: 34 }} />
          <h1 style={styles.title}>QEN Admin Paneli</h1>
          <form onSubmit={handleLogin}>
            {loginState.locked ? (
              <div style={{ padding: 20, background: '#fee2e2', borderRadius: 10, marginBottom: 15 }}>
                <p style={{ color: '#dc2626', fontWeight: 'bold', margin: 0 }}>Hesap kilitli</p>
                <p style={{ color: '#dc2626', margin: '8px 0 0' }}>
                  {Math.floor(loginState.lockSeconds / 60)}:{String(loginState.lockSeconds % 60).padStart(2, '0')} kaldi
                </p>
              </div>
            ) : loginState.requiresVerification ? (
              <>
                <p style={{ color: '#dc2626', marginBottom: 10, fontSize: 14 }}>Dogrulama kodu gerekli. E-postanizi kontrol edin.</p>
                <input type="password" placeholder="Sifre" value={password} onChange={(e) => setPassword(e.target.value)} style={styles.input} data-testid="admin-password-input" autoFocus />
                <input type="text" placeholder="Dogrulama Kodu" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} style={{ ...styles.input, letterSpacing: 8, textAlign: 'center', fontSize: 22 }} data-testid="admin-verification-input" />
                <button type="submit" style={styles.button} data-testid="admin-login-btn">Dogrula ve Giris</button>
                <button type="button" onClick={() => axios.post(`${API}/auth/resend-code`).then(() => setError("Yeni kod gonderildi")).catch(() => {})} style={{ ...styles.backButton, marginTop: 10 }}>Kodu Tekrar Gonder</button>
              </>
            ) : (
              <>
                <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>Sifrenizi girin</p>
                <input type="password" placeholder="Sifre" value={password} onChange={(e) => setPassword(e.target.value)} style={styles.input} data-testid="admin-password-input" autoFocus />
                <button type="submit" style={{ ...styles.button, opacity: loginLoading ? 0.6 : 1 }} data-testid="admin-login-btn" disabled={loginLoading}>{loginLoading ? "Giris yapiliyor..." : "Giris"}</button>
                {loginState.remaining !== null && loginState.remaining <= 3 && (
                  <p style={{ color: '#f59e0b', marginTop: 10, fontSize: 14 }}>{loginState.remaining} hak kaldi</p>
                )}
              </>
            )}
          </form>
          {error && <p style={styles.error} data-testid="admin-login-error">{error}</p>}
        </div>
      </div>
    );
  }

  // Step 2: Branch selection (only for ana/master)
  if ((accessType === "ana" || accessType === "master") && !profile) {
    return (
      <div className={rootClass} style={styles.container}>
        <div style={styles.loginBox}>
          <ThemeToggleBtn style={{ position: 'absolute', top: 16, right: 16, width: 34, height: 34 }} />
          <h1 style={styles.title}>QEN Admin Paneli</h1>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            {accessType === "master" ? "Master Erisim" : "Ana Sifre Erisimi"} - Sube Secin
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {availableProfiles.map(u => (
              <button key={u.profile} onClick={() => selectProfile(u.profile)}
                style={{ ...styles.button, background: '#334155', color: '#fff' }} data-testid={`select-profile-${u.profile}`}>
                {u.name}
              </button>
            ))}
          </div>
          <button onClick={() => { setAccessType(null); setPassword(""); setAvailableProfiles([]); }} style={{ ...styles.backButton, marginTop: '15px' }} data-testid="back-to-login">Geri</button>
        </div>
      </div>
    );
  }

  // Step 3: Logged in - Show tabs
  const currentUserName = users.find(u => u.profile === profile)?.name || profile;
  const isMaster = accessType === "master";
  const canSwitchProfile = accessType === "ana" || accessType === "master";

  const isLegacyPricingTab = ['ozan_marj', 'tl_iscilik', 'has', 'ozel'].includes(activeNav);
  const isAkTab = activeNav === 'altinkaynak_carpan';
  const isFiyatKaynagiTab = activeNav === 'fiyat_kaynagi';
  const forcedView = isLegacyPricingTab ? activeNav : 'ozan_marj';

  // Bu sube baska bir kaynaga bagli mi?
  const isMirrored = priceSource.source_profile !== null;
  const mirrorLabel = priceSource.source_profile === "altinkaynak"
    ? "Altınkaynak (Ham)"
    : ({ default: "Ana Sayfa", "1": "Ufuktepe", "2": "Dutluk" })[priceSource.source_profile] || priceSource.source_profile;

  const allDigerItems = [
    ...DIGER_ITEMS,
    ...(isMaster ? [{ id: 'kullanici', label: 'Kullanici', icon: Settings }] : []),
    ...((accessType === "ana" || isMaster) ? [{ id: 'erisim_kodlari', label: 'Erisim Kodlari', icon: KeyRound }] : []),
  ];

  const MirrorBanner = () => (
    <div style={{
      margin: '14px 18px 0', padding: '12px 16px',
      background: 'var(--adm-card)', border: '1px solid #DA9F54', borderRadius: '10px',
      color: '#DA9F54', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px',
    }} data-testid="mirror-banner">
      <Link2 size={18} />
      <div>
        Bu şube şu an <strong>{mirrorLabel}</strong> kaynağına bağlı. Bu tab'daki ayarlar bu şube için <strong>kullanılmıyor</strong>.
        Değiştirmek için sol menüden <strong>Fiyat Kaynağı</strong> sekmesine gidin.
      </div>
    </div>
  );

  // Shared content renderer
  const renderContent = () => (
    <>
      {isMirrored && (isLegacyPricingTab || isAkTab) && <MirrorBanner />}
      <div style={{ display: isLegacyPricingTab ? 'block' : 'none', opacity: isMirrored ? 0.5 : 1, pointerEvents: isMirrored ? 'none' : 'auto' }}>
        <MarjTab profile={profile} users={users} currentUserName={currentUserName} forcedView={forcedView} isDesktop={isDesktop} onActivate={setActivePricingMode} />
      </div>
      {isAkTab && (
        <div style={{ opacity: isMirrored ? 0.5 : 1, pointerEvents: isMirrored ? 'none' : 'auto' }}>
          <AltinkaynakCarpanTab profile={profile} pricingModeActive={activePricingMode} onActivate={setActivePricingMode} />
        </div>
      )}
      {isFiyatKaynagiTab && <FiyatKaynagiTab profile={profile} />}
      {activeNav === 'ticker' && <TickerTab profile={profile} currentUserName={currentUserName} />}
      {activeNav === 'arayuz' && <ArayuzTab profile={profile} currentUserName={currentUserName} />}
      {activeNav === 'fiyat_guvenligi' && <FiyatGuvenligiTab profile={profile} />}
      {activeNav === 'alarm' && <AlarmTab profile={profile} />}
      {activeNav === 'eposta' && <EpostaTab profile={profile} />}
      {activeNav === 'kullanici' && isMaster && <KullaniciTab profile={profile} currentUserName={currentUserName} users={users} onUsersChange={loadUsers} />}
      {activeNav === 'erisim_kodlari' && (accessType === 'ana' || accessType === 'master') && <ErisimKodlariTab auth={password} />}
    </>
  );

  // ─── DESKTOP: Sidebar + Content ───
  if (isDesktop) {
    return (
      <div className={rootClass} style={{ display: 'flex', minHeight: '100vh', background: 'var(--adm-bg, #0e1512)' }}>
        {/* Left Sidebar */}
        <aside style={{
          width: SIDEBAR_W, minWidth: SIDEBAR_W, background: 'var(--adm-sidebar)',
          borderRight: '1px solid var(--adm-sidebar-border)', display: 'flex', flexDirection: 'column',
          position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50,
          overflowY: 'auto',
        }}>
          {/* Sidebar Header */}
          <div style={{ padding: '18px 16px 12px', borderBottom: '1px solid var(--adm-sidebar-border)' }}>
            <div style={{ color: '#DA9F54', fontWeight: 'bold', fontSize: '15px', marginBottom: '4px' }}>{currentUserName}</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {accessType === "master" && <span style={{ color: '#22c55e', fontSize: '10px', fontWeight: 'bold', background: '#0a2a1a', padding: '2px 8px', borderRadius: '4px' }}>MASTER</span>}
              {accessType === "ana" && <span style={{ color: '#3b82f6', fontSize: '10px', fontWeight: 'bold', background: '#0a1a2a', padding: '2px 8px', borderRadius: '4px' }}>ANA</span>}
            </div>
          </div>

          {/* Pricing modes section */}
          <div style={{ padding: '14px 12px 6px' }}>
            <p style={{ color: 'var(--adm-sidebar-label)', fontSize: '10px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '8px', paddingLeft: '4px' }}>Fiyatlandirma</p>
            {NAV_ITEMS.map(item => {
              const isActive = activeNav === item.id || (item.isGroup && ESKI_MODLAR_ITEMS.some(s => s.id === activeNav));
              const Icon = item.icon;
              if (item.isGroup) {
                const isEskiActive = ESKI_MODLAR_ITEMS.some(s => s.id === activeNav);
                return (
                  <div key={item.id}>
                    <button onClick={() => setEskiModlarOpen(o => !o)} data-testid={`sidebar-nav-${item.id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                        padding: '10px 12px', marginBottom: '2px', border: 'none', cursor: 'pointer',
                        borderRadius: '8px', transition: 'all 0.2s',
                        backgroundColor: isEskiActive ? '#DA9F54' : 'transparent',
                        color: isEskiActive ? '#000' : 'var(--adm-sidebar-item)',
                      }}>
                      <Icon size={17} strokeWidth={isEskiActive ? 2.4 : 1.6} />
                      <span style={{ fontSize: '13px', fontWeight: isEskiActive ? '700' : '500', flex: 1, textAlign: 'left' }}>{item.label}</span>
                      <span style={{ fontSize: '14px', transform: eskiModlarOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▸</span>
                    </button>
                    {eskiModlarOpen && ESKI_MODLAR_ITEMS.map(sub => {
                      const SubIcon = sub.icon;
                      const subActive = activeNav === sub.id;
                      return (
                        <button key={sub.id} onClick={() => setActiveNav(sub.id)} data-testid={`sidebar-nav-${sub.id}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                            padding: '8px 12px 8px 28px', marginBottom: '2px', border: 'none', cursor: 'pointer',
                            borderRadius: '8px', transition: 'all 0.2s',
                            backgroundColor: subActive ? '#DA9F54' : 'transparent',
                            color: subActive ? '#000' : 'var(--adm-sidebar-item)',
                          }}>
                          <SubIcon size={15} strokeWidth={subActive ? 2.4 : 1.6} />
                          <span style={{ fontSize: '12px', fontWeight: subActive ? '700' : '500' }}>{sub.label}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              }
              return (
                <button key={item.id} onClick={() => setActiveNav(item.id)} data-testid={`sidebar-nav-${item.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '10px 12px', marginBottom: '2px', border: 'none', cursor: 'pointer',
                    borderRadius: '8px', transition: 'all 0.2s',
                    backgroundColor: isActive ? '#DA9F54' : 'transparent',
                    color: isActive ? '#000' : 'var(--adm-sidebar-item)',
                  }}>
                  <Icon size={17} strokeWidth={isActive ? 2.4 : 1.6} />
                  <span style={{ fontSize: '13px', fontWeight: isActive ? '700' : '500' }}>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Diger Ayarlar section */}
          <div style={{ padding: '10px 12px 6px' }}>
            <p style={{ color: 'var(--adm-sidebar-label)', fontSize: '10px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '8px', paddingLeft: '4px' }}>Diger Ayarlar</p>
            {allDigerItems.map(item => {
              const isActive = activeNav === item.id;
              const Icon = item.icon;
              return (
                <button key={item.id} onClick={() => setActiveNav(item.id)} data-testid={`sidebar-nav-${item.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    padding: '10px 12px', marginBottom: '2px', border: 'none', cursor: 'pointer',
                    borderRadius: '8px', transition: 'all 0.2s',
                    backgroundColor: isActive ? '#DA9F54' : 'transparent',
                    color: isActive ? '#000' : 'var(--adm-sidebar-item)',
                  }}>
                  <Icon size={17} strokeWidth={isActive ? 2.4 : 1.6} />
                  <span style={{ fontSize: '13px', fontWeight: isActive ? '700' : '500' }}>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Bottom actions */}
          <div style={{ padding: '12px', borderTop: '1px solid var(--adm-sidebar-border)' }}>
            <button onClick={toggleAdminTheme} data-testid="admin-theme-toggle"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 12px', marginBottom: '6px', background: 'var(--adm-btn-bg)', color: '#DA9F54', border: '1px solid var(--adm-btn-border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
              {adminTheme === 'dark' ? <Sun size={14} /> : <Moon size={14} />} {adminTheme === 'dark' ? 'Açık Tema' : 'Koyu Tema'}
            </button>
            {canSwitchProfile && (
              <button onClick={handleBackToProfiles} data-testid="switch-branch-btn"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 12px', marginBottom: '6px', background: 'var(--adm-btn-bg)', color: '#DA9F54', border: '1px solid var(--adm-btn-border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                <ArrowLeftRight size={14} /> Sube Degistir
              </button>
            )}
            <button onClick={handleLogout} data-testid="logout-btn"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 12px', background: '#2a1515', color: '#e57373', border: '1px solid #4a2020', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
              <LogOut size={14} /> Guvenli Cikis
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main style={{ marginLeft: SIDEBAR_W, flex: 1, minHeight: '100vh', overflowY: 'auto' }}>
          {renderContent()}
        </main>
      </div>
    );
  }

  // ─── MOBILE: Bottom Tab Bar ───
  // NAV_ITEMS'tan group olanlari acalim (mobilde tum modlari ayri tab gibi gosteriyoruz)
  const mobileBottomTabs = [
    { id: 'altinkaynak_carpan', label: 'AK HAS', icon: Sparkles },
    { id: 'fiyat_kaynagi', label: 'Fiyat Kaynagi', icon: Link2 },
    { id: 'diger', label: 'Diger', icon: Settings },
  ];

  return (
    <div className={rootClass} style={{ minHeight: "100vh", background: "var(--adm-bg, #0e1512)", paddingBottom: "80px" }}>
      {/* Mobile Top Header */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '10px 16px', backgroundColor: 'var(--adm-header)', borderBottom: '1px solid var(--adm-header-border)', flexWrap: 'wrap' }}>
        <span style={{ color: '#DA9F54', fontWeight: 'bold', fontSize: '15px' }}>{currentUserName}</span>
        {accessType === "master" && <span style={{ color: '#22c55e', fontSize: '11px', fontWeight: 'bold', background: '#0a2a1a', padding: '2px 8px', borderRadius: '5px' }}>MASTER</span>}
        {accessType === "ana" && <span style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 'bold', background: '#0a1a2a', padding: '2px 8px', borderRadius: '5px' }}>ANA</span>}
        <button onClick={toggleAdminTheme} data-testid="admin-theme-toggle" title="Tema"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'var(--adm-btn-bg)', color: '#DA9F54', border: '1px solid #DA9F54', borderRadius: '6px', cursor: 'pointer' }}>
          {adminTheme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>
        {canSwitchProfile && (
          <button onClick={handleBackToProfiles} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px', background: 'var(--adm-btn-bg)', color: '#DA9F54', border: '1px solid #DA9F54', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }} data-testid="switch-branch-btn">
            <ArrowLeftRight size={13} /> Sube
          </button>
        )}
        <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px', background: '#333', color: '#ccc', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }} data-testid="logout-btn">
          <LogOut size={13} /> Cikis
        </button>
      </div>

      {/* Mobile Content */}
      {isLegacyPricingTab && renderContent()}
      {isAkTab && renderContent()}
      {isFiyatKaynagiTab && renderContent()}
      {!isLegacyPricingTab && !isAkTab && !isFiyatKaynagiTab && activeNav !== 'diger' && activeNav !== 'eski_modlar_mobile' && renderContent()}

      {/* Diger sub-tab mobile view */}
      {activeNav === 'diger' && (
        <MobileDigerView digerItems={allDigerItems} setActiveNav={setActiveNav} />
      )}

      {/* Eski Modlar sub-tab mobile view (4 mod listesi) */}
      {activeNav === 'eski_modlar_mobile' && (
        <MobileDigerView digerItems={ESKI_MODLAR_ITEMS} setActiveNav={setActiveNav} />
      )}

      {/* Mobile Bottom Navigation Bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
        display: 'flex', justifyContent: 'space-around', alignItems: 'center',
        backgroundColor: 'var(--adm-bottomnav)', borderTop: '1px solid var(--adm-bottomnav-border)',
        padding: '6px 0 env(safe-area-inset-bottom, 8px)',
        overflowX: 'auto',
      }}>
        {mobileBottomTabs.map(tab => {
          const isActive = activeNav === tab.id;
          const isDiGerActive = tab.id === 'diger' && [...DIGER_ITEMS.map(d => d.id), 'kullanici', 'erisim_kodlari'].includes(activeNav);
          const isEskiActive = tab.id === 'eski_modlar_mobile' && ESKI_MODLAR_ITEMS.some(s => s.id === activeNav);
          const active = isActive || isDiGerActive || isEskiActive;
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveNav(tab.id)} data-testid={`bottom-tab-${tab.id}`}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                padding: '6px 8px', border: 'none', cursor: 'pointer',
                backgroundColor: active ? 'rgba(218, 159, 84, 0.15)' : 'transparent',
                borderRadius: '12px', transition: 'all 0.25s ease', minWidth: '54px', flexShrink: 0,
              }}>
              <Icon size={22} color={active ? '#DA9F54' : '#666'} strokeWidth={active ? 2.5 : 1.8} />
              <span style={{
                fontSize: '10px', fontWeight: active ? '700' : '500',
                color: active ? '#DA9F54' : '#666',
              }}>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Mobile Diger items grid
const MobileDigerView = ({ digerItems, setActiveNav }) => (
  <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
    {digerItems.map(item => {
      const Icon = item.icon;
      return (
        <button key={item.id} onClick={() => setActiveNav(item.id)} data-testid={`diger-mobile-${item.id}`}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
            padding: '20px 12px', background: 'var(--adm-card)', border: '1px solid var(--adm-border)',
            borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s',
          }}>
          <Icon size={28} color="#DA9F54" strokeWidth={1.6} />
          <span style={{ color: '#DA9F54', fontSize: '13px', fontWeight: '600' }}>{item.label}</span>
        </button>
      );
    })}
  </div>
);

export default AdminPage;
